import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Layout as AntLayout,
  Menu,
  Button,
  Avatar,
  Space,
  Typography,
  Drawer,
  theme,
} from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  TeamOutlined,
  UserAddOutlined,
  LogoutOutlined,
  UnorderedListOutlined,
  SettingOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import Grid from 'antd/es/grid';
import { useState } from 'react';

const { useBreakpoint } = Grid;
import { useAuthStore } from '../store/auth';
import { logout } from '../lib/api';
import { getUserDisplayName } from '../lib/user';

const { Sider, Content, Header } = AntLayout;
const { Text } = Typography;

export default function Layout() {
  const { user, logout: logoutStore } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const { token } = theme.useToken();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  // Breakpoint detection
  const isMobile = !screens.md; // < 768px
  const isTablet = screens.md && !screens.lg; // 768px - 991px

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Ignore error
    }
    logoutStore();
    navigate('/login');
  };

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'boss':
        return '老闆';
      case 'manager':
        return '主管';
      case 'employee':
        return '員工';
      default:
        return '';
    }
  };

  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: '儀表板',
    },
    {
      key: '/tasks',
      icon: <UnorderedListOutlined />,
      label: '任務管理',
    },
    {
      key: '/reports',
      icon: <FileTextOutlined />,
      label: '週報管理',
    },
    ...(user?.role === 'boss' || user?.role === 'manager'
      ? [
          {
            key: '/admin/create-user',
            icon: <UserAddOutlined />,
            label: '建立使用者',
          },
          {
            key: '/groups',
            icon: <TeamOutlined />,
            label: '群組管理',
          },
        ]
      : []),
    ...(user?.role === 'boss'
      ? [
          {
            key: '/admin/users',
            icon: <SettingOutlined />,
            label: '使用者管理',
          },
        ]
      : []),
  ];

  // Navigation menu component (reused in Sider and Drawer)
  const NavigationMenu = ({
    inDrawer = false,
    isTablet: navIsTablet = false,
    collapsed: navCollapsed = false,
  }: {
    inDrawer?: boolean;
    isTablet?: boolean;
    collapsed?: boolean;
  }) => {
    // Tablet expanded: horizontal layout with avatar only and circular logout button
    if (navIsTablet && !navCollapsed) {
      return (
        <>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={({ key }) => navigate(key)}
            style={{
              flex: 1,
              borderRight: 0,
            }}
          />

          <div
            style={{
              padding: '12px 16px',
              borderTop: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Link
                to="/profile"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = token.colorBgTextHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Avatar style={{ backgroundColor: token.colorPrimary }}>
                  {getUserDisplayName(user).charAt(0).toUpperCase()}
                </Avatar>
              </Link>
              <Button
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                shape="circle"
                type="default"
                title="登出"
              />
            </div>
          </div>
        </>
      );
    }

    // Tablet collapsed: avatar only with logout icon below
    if (navIsTablet && navCollapsed) {
      return (
        <>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={({ key }) => navigate(key)}
            style={{
              flex: 1,
              borderRight: 0,
            }}
          />

          <div
            style={{
              padding: '12px 0',
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Link
              to="/profile"
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = token.colorBgTextHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Avatar style={{ backgroundColor: token.colorPrimary }}>
                {getUserDisplayName(user).charAt(0).toUpperCase()}
              </Avatar>
            </Link>
            <Button
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              shape="circle"
              type="text"
              size="small"
              title="登出"
            />
          </div>
        </>
      );
    }

    // Default (Desktop or Mobile Drawer): vertical layout
    return (
      <>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => {
            navigate(key);
            if (inDrawer) {
              setDrawerOpen(false);
            }
          }}
          style={{
            flex: 1,
            borderRight: 0,
            background: inDrawer ? 'transparent' : undefined,
          }}
        />

        <div
          style={{
            padding: '16px',
            borderTop: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Link
              to="/profile"
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                padding: '8px',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = token.colorBgTextHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onClick={() => {
                if (inDrawer) {
                  setDrawerOpen(false);
                }
              }}
            >
              <Space>
                <Avatar style={{ backgroundColor: token.colorPrimary }}>
                  {getUserDisplayName(user).charAt(0).toUpperCase()}
                </Avatar>
                <div>
                  <div style={{ fontWeight: 500 }}>{getUserDisplayName(user)}</div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {getRoleLabel(user?.role)}
                  </Text>
                </div>
              </Space>
            </Link>
            <Button
              icon={<LogoutOutlined />}
              onClick={() => {
                if (inDrawer) {
                  setDrawerOpen(false);
                }
                handleLogout();
              }}
              block
              type="default"
            >
              登出
            </Button>
          </Space>
        </div>
      </>
    );
  };

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      {/* Mobile Header */}
      {isMobile && (
        <Header
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: token.colorBgContainer,
            padding: '0 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            height: 56,
          }}
        >
          <Button
            type="text"
            icon={<MenuOutlined style={{ fontSize: 18 }} />}
            onClick={() => setDrawerOpen(true)}
            style={{ width: 44, height: 44 }}
          />
          <h1
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 600,
              color: token.colorText,
            }}
          >
            任務回報系統
          </h1>
          <Avatar
            size="small"
            style={{
              backgroundColor: token.colorPrimary,
              width: 32,
              height: 32,
            }}
          >
            {getUserDisplayName(user).charAt(0).toUpperCase()}
          </Avatar>
        </Header>
      )}

      {/* Mobile Drawer */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        placement="left"
        size="default"
        styles={{
          wrapper: {
            width: 280,
          },
          body: {
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },
        }}
        closeIcon={null}
      >
        <div
          style={{
            padding: '20px 16px',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: 600,
              color: token.colorText,
            }}
          >
            任務回報系統
          </h1>
        </div>
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <NavigationMenu inDrawer />
        </div>
      </Drawer>

      {/* Tablet/Desktop Sider */}
      {!isMobile && (
        <Sider
          width={250}
          theme="light"
          collapsible={isTablet}
          collapsed={isTablet && collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          style={{
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              padding: collapsed ? '16px 8px' : '24px 16px',
              borderBottom: '1px solid #f0f0f0',
              textAlign: collapsed ? 'center' : 'left',
            }}
          >
            {collapsed && isTablet ? (
              <h1
                style={{
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: 600,
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  display: 'inline-block',
                }}
              >
                任務回報
              </h1>
            ) : (
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>任務回報系統</h1>
            )}
          </div>

          <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            <NavigationMenu isTablet={isTablet} collapsed={collapsed} />
          </div>
        </Sider>
      )}

      {/* Content Area */}
      <Content
        style={{
          padding: isMobile ? '16px' : '24px',
          marginTop: isMobile ? 56 : 0,
          background: token.colorBgLayout,
          overflow: 'auto',
        }}
      >
        <Outlet />
      </Content>
    </AntLayout>
  );
}
